import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Snackbar from '@mui/material/Snackbar';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useState } from 'react';

import { ToastContext } from './ToastContext';

import type { AlertColor } from '@mui/material/Alert';
import type { ReactNode, SyntheticEvent } from 'react';
import type { ToastOptions } from './Toast.types';

interface ToastState {
    open: boolean;
    message: string;
    severity: AlertColor;
    title?: string | undefined;
    duration: number;
    position: {
        vertical: 'top' | 'bottom';
        horizontal: 'left' | 'center' | 'right';
    };
    action?: ReactNode;
    id: number;
}

export interface ToastProviderProps {
    children: ReactNode;
}

/**
 * Toast Provider Component
 *
 * Wrap your app with this provider to enable toast notifications.
 */
export function ToastProvider({ children }: ToastProviderProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [toast, setToast] = useState<ToastState>({
        open: false,
        message: '',
        severity: 'info',
        duration: 4000,
        position: { vertical: 'top', horizontal: 'right' },
        id: 0,
    });

    const showToast = useCallback((options: ToastOptions) => {
        setToast(prev => ({
            open: true,
            message: options.message,
            severity: options.severity || 'info',
            title: options.title || undefined,
            duration: options.duration ?? 4000,
            position: options.position || { vertical: 'top', horizontal: 'right' },
            action: options.action || undefined,
            id: prev.id + 1,
        }));
    }, []);

    const closeToast = useCallback(() => {
        setToast(prev => ({ ...prev, open: false }));
    }, []);

    const success = useCallback(
        (message: string, title?: string) => {
            showToast({ message, ...(title && { title }), severity: 'success' });
        },
        [showToast]
    );

    const error = useCallback(
        (message: string, title?: string) => {
            showToast({ message, ...(title && { title }), severity: 'error', duration: 6000 });
        },
        [showToast]
    );

    const warning = useCallback(
        (message: string, title?: string) => {
            showToast({ message, ...(title && { title }), severity: 'warning', duration: 5000 });
        },
        [showToast]
    );

    const info = useCallback(
        (message: string, title?: string) => {
            showToast({ message, ...(title && { title }), severity: 'info' });
        },
        [showToast]
    );

    const handleClose = (_event?: SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        closeToast();
    };

    return (
        <ToastContext.Provider value={{ showToast, success, error, warning, info, closeToast }}>
            {children}
            <Snackbar
                key={toast.id}
                open={toast.open}
                autoHideDuration={toast.duration}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: toast.position.vertical,
                    horizontal: isMobile ? 'center' : (toast.position?.horizontal ?? 'right'),
                }}
            >
                <Alert
                    tabIndex={0}
                    aria-label="Notification details"
                    onClose={handleClose}
                    severity={toast.severity || 'info'}
                    variant="filled"
                    {...(toast.action && { action: toast.action })}
                    sx={{
                        width: '100%',
                        maxWidth: { xs: 'calc(100vw - 32px)', sm: 560 },
                        maxHeight: 'calc(100dvh - 48px)',
                        overflowY: 'auto',
                        boxShadow: 3,
                        outline: 'none',
                        '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: 2,
                        },
                        '& .MuiAlert-message': {
                            width: '100%',
                            overflow: 'visible',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        },
                    }}
                >
                    {toast.title && <AlertTitle>{toast.title}</AlertTitle>}
                    {toast.message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
}


